import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { employeeApi, companyApi } from '@/lib/api';
import { User, Location } from '@/types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { formatDate, getRoleColor } from '@/lib/utils';
import { 
  MagnifyingGlassIcon, 
  UserPlusIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  XMarkIcon,
  CheckIcon,
  DocumentArrowDownIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import JoiningLetterModal from '@/components/JoiningLetterModal';
import RelievingLetterModal from '@/components/RelievingLetterModal';

// Employee Form Component
interface EmployeeFormProps {
  employee?: User;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  loading: boolean;
}

function EmployeeForm({ employee, onSubmit, onCancel, loading }: EmployeeFormProps) {
  const [companyLocations, setCompanyLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  
  const [formData, setFormData] = useState({
    firstName: employee?.firstName || '',
    lastName: employee?.lastName || '',
    email: employee?.email || '',
    password: '',
    phone: employee?.phone || '',
    department: employee?.department || '',
    position: employee?.position || '',
    salary: employee?.salary || 0,
    salaryType: employee?.salaryType || 'MONTHLY',
    role: employee?.role || 'EMPLOYEE',
    address: {
      street: employee?.address?.street || '',
      city: employee?.address?.city || '',
      state: employee?.address?.state || '',
      zipCode: employee?.address?.zipCode || '',
      country: employee?.address?.country || 'India'
    },
    emergencyContact: {
      name: employee?.emergencyContact?.name || '',
      relationship: employee?.emergencyContact?.relationship || '',
      phone: employee?.emergencyContact?.phone || ''
    },
    workLocation: {
      latitude: employee?.workLocation?.latitude || 0,
      longitude: employee?.workLocation?.longitude || 0,
      address: employee?.workLocation?.address || ''
    }
  });

  // Fetch company locations when component mounts
  useEffect(() => {
    const fetchCompanyLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await companyApi.getCompany();
        if (response.success && response.data?.company?.locations) {
          const activeLocations = response.data.company.locations.filter((loc: Location) => loc.isActive);
          setCompanyLocations(activeLocations);
          
          // If editing employee, find matching location
          if (employee?.workLocation) {
            console.log('Employee work location:', employee.workLocation);
            const matchingLocation = activeLocations.find(
              (loc: Location) => {
                console.log('Comparing location:', loc.coordinates, 'with employee location:', employee.workLocation);
                return Math.abs(loc.coordinates.latitude - employee.workLocation.latitude) < 0.0001 &&
                       Math.abs(loc.coordinates.longitude - employee.workLocation.longitude) < 0.0001;
              }
            );
            console.log('Matching location found:', matchingLocation);
            if (matchingLocation && matchingLocation._id) {
              setSelectedLocationId(matchingLocation._id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching company locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    };
    
    fetchCompanyLocations();
  }, [employee]);

  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId);
    
    if (locationId) {
      const selectedLocation = companyLocations.find((loc) => loc._id === locationId);
      if (selectedLocation) {
        setFormData(prev => ({
          ...prev,
          workLocation: {
            latitude: selectedLocation.coordinates.latitude,
            longitude: selectedLocation.coordinates.longitude,
            address: `${selectedLocation.address.street}, ${selectedLocation.address.city}, ${selectedLocation.address.state}, ${selectedLocation.address.zipCode}, ${selectedLocation.address.country}`
          }
        }));
      }
    } else {
      // Reset work location if no location selected
      setFormData(prev => ({
        ...prev,
        workLocation: {
          latitude: 0,
          longitude: 0,
          address: ''
        }
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (employee && !submitData.password) {
      // @ts-ignore
      delete submitData.password;
    }
    onSubmit(submitData);
  };

  const handleChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          // @ts-ignore
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-md bg-black/20 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border-0 transform transition-all duration-300 ease-out scale-100">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {employee ? 'Edit Employee' : 'Add New Employee'}
            </h2>
            <Button variant="outline" onClick={onCancel} className="rounded-full hover:bg-gray-100">
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <Input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <Input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  disabled={!!employee}
                />
              </div>
              {!employee && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    required={!employee}
                    placeholder="Minimum 6 characters"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone *
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department *
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                > 
                  <option value="">Select Department</option>
                  <option value="IT">IT</option>
                  <option value="DATAENTRY">DATAENTRY</option>
                  <option value="HR">HR</option>
                  <option value="FINANCE">FINANCE</option>
                  <option value="SALES">SALES</option>
                  <option value="ADMIN">ADMIN</option>
                 
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position *
                </label>
                <Input
                  type="text"
                  value={formData.position}
                  onChange={(e) => handleChange('position', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="EMPLOYEE">Employee</option>
                  <option value="MANAGER">Manager</option>
                  <option value="HR">HR</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salary *
                </label>
                <Input
                  type="number"
                  value={formData.salary}
                  onChange={(e) => handleChange('salary', Number(e.target.value))}
                  required
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salary Type *
                </label>
                <select
                  value={formData.salaryType}
                  onChange={(e) => handleChange('salaryType', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="HOURLY">Hourly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-100 pb-2">Address Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Address *
                  </label>
                  <Input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) => handleChange('address.street', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City *
                  </label>
                  <Input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => handleChange('address.city', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State *
                  </label>
                  <Input
                    type="text"
                    value={formData.address.state}
                    onChange={(e) => handleChange('address.state', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zip Code *
                  </label>
                  <Input
                    type="text"
                    value={formData.address.zipCode}
                    onChange={(e) => handleChange('address.zipCode', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country *
                  </label>
                  <Input
                    type="text"
                    value={formData.address.country}
                    onChange={(e) => handleChange('address.country', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-100 pb-2">Emergency Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.emergencyContact.name}
                    onChange={(e) => handleChange('emergencyContact.name', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relationship *
                  </label>
                  <Input
                    type="text"
                    value={formData.emergencyContact.relationship}
                    onChange={(e) => handleChange('emergencyContact.relationship', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <Input
                    type="number"
                    value={formData.emergencyContact.phone}
                    onChange={(e) => handleChange('emergencyContact.phone', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-100 pb-2">Work Location</h3>
              {loadingLocations ? (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">Loading locations...</p>
                </div>
              ) : companyLocations.length > 0 ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Work Location *
                    </label>
                    <select
                      value={selectedLocationId}
                      onChange={(e) => handleLocationChange(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a location</option>
                      {companyLocations.map((location) => (
                        <option key={location._id} value={location._id}>
                          {location.name} - {location.address.city}, {location.address.state}
                          {location.isMainOffice ? ' (Main Office)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedLocationId && (
                    <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                      <p className="text-sm font-medium text-gray-700">Selected Location Details:</p>
                      <p className="text-sm text-gray-600">{formData.workLocation.address}</p>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                        <p>Latitude: {formData.workLocation.latitude}</p>
                        <p>Longitude: {formData.workLocation.longitude}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">
                    No company locations available. Please add locations in Company Settings first.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={onCancel} className="hover:bg-gray-50">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 shadow-lg">
                {loading ? 'Saving...' : employee ? 'Update Employee' : 'Add Employee'}
              </Button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
}

export default function EmployeesPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<User | undefined>(undefined);
  const [formLoading, setFormLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<User | undefined>(undefined);
  const [joiningLetterEmployee, setJoiningLetterEmployee] = useState<User | undefined>(undefined);
  const [relievingLetterEmployee, setRelievingLetterEmployee] = useState<User | undefined>(undefined);

  console.log("EmployeesPage render--, employees:", employees);

  useEffect(() => {
    if (user && (user.role === 'ADMIN' || user.role === 'HR' || user.role === 'SUPERADMIN')) {
      fetchEmployees();
    }
  }, [user, page, search, department]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeApi.getAll({
        page,
        limit: 10, // Changed to 10 per page as requested
        search: search || undefined,
        department: department || undefined,
      });

      if (response.success && response.data) {
        console.log('Fetched employees:', response.data);
        setEmployees(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.pages || Math.ceil(response.pagination.total / 10));
          setTotalEmployees(response.pagination.total || response.data.length);
        } else {
          setTotalEmployees(response.data.length);
        }
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Reset to first page and let the effect fetch employees (avoids using stale page state)
    setPage(1);
  };

  const handleAddEmployee = () => {
  setEditingEmployee(undefined);
    setShowForm(true);
  };

  const handleEditEmployee = (employee: User) => {
    console.log('Editing employee--:', employee);
    setEditingEmployee(employee);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData: any) => {
    try {
      setFormLoading(true);
      
      if (editingEmployee) {
        console.log('Updating employee with data:==', formData);
        delete formData.email;
        const response = await employeeApi.update(editingEmployee._id, formData);
        if (response.success) {
          await fetchEmployees();
          setShowForm(false);
          setEditingEmployee(undefined);
          alert('Employee updated successfully!');
        }
      } else {
        const response = await employeeApi.create(formData);
        if (response.success) {
          await fetchEmployees();
          setShowForm(false);
          alert('Employee added successfully!');
        }
      }
    } catch (error:any) {
      console.log(error)
      console.error('Error saving employee:', error);
      alert(error.response?.data?.error || 'Error saving employee. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteEmployee = async (employee: User) => {
    if (confirm(`Are you sure you want to deactivate ${employee.firstName} ${employee.lastName}?`)) {
      try {
        const response = await employeeApi.deactivate(employee._id);
        if (response.success) {
          await fetchEmployees();
          alert('Employee deactivated successfully!');
        }
      } catch (error) {
        console.error('Error deactivating employee:', error);
        alert('Error deactivating employee. Please try again.');
      }
    }
  };

  const handleActivateEmployee = async (employee: User) => {
    try {
      const response = await employeeApi.activate(employee.id);
      if (response.success) {
        await fetchEmployees();
        alert('Employee activated successfully!');
      }
    } catch (error) {
      console.error('Error activating employee:', error);
      alert('Error activating employee. Please try again.');
    }
  };

  const handleViewEmployee = (employee: User) => {
    setSelectedEmployee(employee);
  };

  // const handleToggleOfferLetter = async (employee: User, newValue: boolean) => {
  //   try {
  //     const response = await employeeApi.toggleOfferLetter(employee._id, newValue);
  //     if (response.success) {
  //       // Update the selected employee state
  //       setSelectedEmployee(response.data);
  //       // Refresh the employee list
  //       await fetchEmployees();
  //       alert(`Offer letter ${newValue ? 'activated' : 'deactivated'} successfully!`);
  //     }
  //   } catch (error) {
  //     console.error('Error toggling offer letter:', error);
  //     alert('Error toggling offer letter status. Please try again.');
  //   }
  // };

  if (!user || (user.role !== 'ADMIN' && user.role !== 'HR' && user.role !== 'SUPERADMIN')) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employee Management</h1>
          <p className="text-gray-600">Manage employee records and information</p>
        </div>
        <Button onClick={handleAddEmployee}>
          <UserPlusIcon className="mr-2 h-4 w-4" />
          Add Employee
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="shadow-sm border-gray-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Search Employees</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search employee ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="w-48">
              <select
                value={department}
                onChange={(e) => {
                  // when changing department, reset to page 1 so user sees first results for that filter
                  setDepartment(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Department</option>
                  <option value="IT">IT</option>
                  <option value="DATAENTRY">DATAENTRY</option>
                  <option value="HR">HR</option>
                  <option value="FINANCE">FINANCE</option>
                  <option value="SALES">SALES</option>
                  <option value="ADMIN">ADMIN</option>
              </select>
            </div>
            <Button type="submit">
              <MagnifyingGlassIcon className="h-4 w-4" />
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Employee List */}
      <Card className="shadow-sm border-gray-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Employee List ({employees.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
          ) : employees.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Salary
                    </th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joining Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-sm font-medium text-white">
                            {employee.firstName[0]}{employee.lastName[0]}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {employee.firstName} {employee.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{employee.email}</div>
                            <div className="text-xs text-gray-400">{employee.employeeId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{employee.department}</div>
                        <div className="text-sm text-gray-500">{employee.position}</div>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">₹{employee.salary?.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">{employee.salaryType}</div>
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleColor(employee.role)}`}>
                          {employee.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          employee.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {employee.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(employee.joiningDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewEmployee(employee)}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                          
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditEmployee(employee)}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          {(user.role === 'ADMIN' || user.role === 'SUPERADMIN') && (
                            <>
                              {employee.isActive ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteEmployee(employee)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleActivateEmployee(employee)}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </Button>
                              )}
                            </>
                          )}
                          {user.role === 'SUPERADMIN' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setJoiningLetterEmployee(employee)}
                                title="Generate joining letter"
                              >
                                <DocumentArrowDownIcon className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setRelievingLetterEmployee(employee)}
                                title="Generate relieving letter"
                              >
                                <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900">No employees found</h3>
              <p className="text-gray-600">Get started by adding your first employee.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0 bg-white px-4 py-3 border border-gray-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">
              Showing {((page - 1) * 10) + 1} to {Math.min(page * 10, totalEmployees)} of {totalEmployees} employees
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              className="hidden sm:inline-flex text-gray-900 border-gray-400 hover:bg-gray-100"
            >
              First
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="text-gray-900 border-gray-400 hover:bg-gray-100"
            >
              Previous
            </Button>
            
            <div className="flex items-center space-x-1">
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 p-0 ${
                      page === pageNum 
                        ? 'bg-blue-600 text-white border-blue-600' 
                        : 'text-gray-900 border-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="text-gray-900 border-gray-400 hover:bg-gray-100"
            >
              Next
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="hidden sm:inline-flex text-gray-900 border-gray-400 hover:bg-gray-100"
            >
              Last
            </Button>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Page</span>
            <select
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
              className="px-2 py-1 border border-gray-400 rounded text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({ length: totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-700">of {totalPages}</span>
          </div>
        </div>
      )}

      {/* Employee Form Modal */}
      {showForm && (
        <EmployeeForm
          employee={editingEmployee}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingEmployee(undefined);
          }}
          loading={formLoading}
        />
      )}

      {/* Employee Details Modal */}
      {selectedEmployee && (
        <div className="fixed inset-0 backdrop-blur-md bg-black/20 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-0 transform transition-all duration-300 ease-out scale-100">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Employee Details</h2>
                <Button variant="outline" onClick={() => setSelectedEmployee(undefined)} className="rounded-full hover:bg-gray-100">
                  <XMarkIcon className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-100 pb-2">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Employee ID</p>
                      <p className="text-sm text-gray-600">{selectedEmployee.employeeId}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Full Name</p>
                      <p className="text-sm text-gray-600">
                        {selectedEmployee.firstName} {selectedEmployee.lastName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Email</p>
                      <p className="text-sm text-gray-600">{selectedEmployee.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Phone</p>
                      <p className="text-sm text-gray-600">{selectedEmployee.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Department</p>
                      <p className="text-sm text-gray-600">{selectedEmployee.department}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Position</p>
                      <p className="text-sm text-gray-600">{selectedEmployee.position}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Role</p>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleColor(selectedEmployee.role)}`}>
                        {selectedEmployee.role}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Joining Date</p>
                      <p className="text-sm text-gray-600">{formatDate(selectedEmployee.joiningDate)}</p>
                    </div>
                    {user?.role === 'SUPERADMIN' && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Salary</p>
                        <p className="text-sm text-gray-600">₹{selectedEmployee.salary?.toLocaleString()} ({selectedEmployee.salaryType})</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-700">Status</p>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        selectedEmployee.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedEmployee.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Offer Letter Toggle */}
                {/* {(user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'SUPERADMIN') && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-100 pb-2">Offer Letter</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Offer Letter Status</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedEmployee.isOfferLetter 
                            ? 'Employee can download their offer letter' 
                            : 'Employee cannot download offer letter'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedEmployee.isOfferLetter || false}
                          onChange={(e) => handleToggleOfferLetter(selectedEmployee, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        <span className="ms-3 text-sm font-medium text-gray-900">
                          {selectedEmployee.isOfferLetter ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </div>
                  </div>
                )} */}

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-100 pb-2">Address</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>{selectedEmployee.address.street}</p>
                    <p>{selectedEmployee.address.city}, {selectedEmployee.address.state}</p>
                    <p>{selectedEmployee.address.zipCode}, {selectedEmployee.address.country}</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-100 pb-2">Emergency Contact</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Name</p>
                      <p className="text-sm text-gray-600">{selectedEmployee.emergencyContact.name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Relationship</p>
                      <p className="text-sm text-gray-600">{selectedEmployee.emergencyContact.relationship}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Phone</p>
                      <p className="text-sm text-gray-600">{selectedEmployee.emergencyContact.phone}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-100 pb-2">Work Location</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>{selectedEmployee.workLocation.address}</p>
                    <p>Lat: {selectedEmployee.workLocation.latitude}, Long: {selectedEmployee.workLocation.longitude}</p>
                  </div>
                </div>

                {user?.role === 'SUPERADMIN' && (
                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      onClick={() => setJoiningLetterEmployee(selectedEmployee)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                      Generate Joining Letter
                    </Button>
                    <Button
                      onClick={() => setRelievingLetterEmployee(selectedEmployee)}
                      variant="outline"
                    >
                      <ArrowRightStartOnRectangleIcon className="h-4 w-4 mr-2" />
                      Generate Relieving Letter
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {joiningLetterEmployee && (
        <JoiningLetterModal
          employee={joiningLetterEmployee}
          onClose={() => setJoiningLetterEmployee(undefined)}
        />
      )}

      {relievingLetterEmployee && (
        <RelievingLetterModal
          employee={relievingLetterEmployee}
          onClose={() => setRelievingLetterEmployee(undefined)}
        />
      )}
    </div>
  );
}
